import { claimExactKeys } from "@/utils/token";
import Cookies from "js-cookie";
import { useMemo } from "react";

/** Claims minted into the Cognito access token by the backend (see cognito-auth.guard.ts). */
export type TTokenClaims = {
  sub?: string;
  email?: string;
  organizationId?: string;
  externalId?: string;
  impersonationUserId?: string;
  iat?: string;
  exp?: string;
};

const TOKEN_CLAIM_KEYS = ["sub", "email", "organizationId", "impersonationUserId", "iat", "exp", "externalId"] as const;

export type TUseTokenClaims = {
  claims: TTokenClaims;
  isImpersonating: boolean;
  impersonationUserId: number | null;
  organizationId: number | null;
  isExpired: boolean;
};

/**
 * Reads the current access token from cookies and exposes its claims — the frontend
 * counterpart of the backend `claimExactKeys` usage. Recomputes only when the raw
 * token string changes.
 */
export const useTokenClaims = (): TUseTokenClaims => {
  return useMemo(() => {
    const accessToken = Cookies.get("match_token");
    if (!accessToken) {
      return { claims: {}, isImpersonating: false, impersonationUserId: null, organizationId: null, isExpired: true };
    }

    const claims = claimExactKeys(accessToken, TOKEN_CLAIM_KEYS) as TTokenClaims;
    const exp = Number(claims.exp);
    const isExpired = !Number.isFinite(exp) || exp <= Date.now() / 1000;

    return {
      claims,
      isImpersonating: Boolean(claims.impersonationUserId),
      impersonationUserId: claims.impersonationUserId ? Number(claims.impersonationUserId) : null,
      organizationId: claims.organizationId ? Number(claims.organizationId) : null,
      isExpired,
    };
  }, []);
};
